import React from "react";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { todayIso } from "@/components/simstats/simStatsUtils";

const PERIODS = [["day", "Giorno"], ["month", "Mese"], ["year", "Anno"], ["12m", "Ultimi 12 mesi"], ["all", "Sempre"]];
const sel = "h-9 bg-background border border-border rounded-md text-sm px-2 max-w-full";

const shift = (period, iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  if (period === "day") d.setDate(d.getDate() + n);
  else if (period === "year") d.setFullYear(d.getFullYear() + n);
  else d.setMonth(d.getMonth() + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const Segmented = ({ items, value, onChange, testid }) => (
  <div className="inline-flex flex-wrap rounded-lg border border-border p-0.5" role="radiogroup" data-testid={testid}>
    {items.map(([k, l]) => <button key={k} type="button" role="radio" aria-checked={value === k} onClick={() => onChange(k)} data-testid={`${testid}-${k || "auto"}`}
      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${value === k ? "bg-primary text-primary-foreground font-medium" : "text-muted-foreground hover:text-foreground"}`}>{l}</button>)}
  </div>
);

const AnchorInput = ({ period, value, onChange }) => {
  if (period === "all") return null;
  const years = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);
  const next = shift(period, value, 1);
  const input = period === "day"
    ? <input type="date" value={value} max={todayIso()} onChange={(e) => e.target.value && onChange(e.target.value)} className={sel} data-testid="simstats-date" />
    : period === "year"
      ? <select value={value.slice(0, 4)} onChange={(e) => onChange(`${e.target.value}-01-01`)} className={sel} data-testid="simstats-year">{years.map((y) => <option key={y} value={y}>{y}</option>)}</select>
      : <input type="month" value={value.slice(0, 7)} max={todayIso().slice(0, 7)} onChange={(e) => e.target.value && onChange(`${e.target.value}-01`)} className={sel} data-testid="simstats-month" />;
  return <div className="flex items-center gap-1">
    <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => onChange(shift(period, value, -1))} aria-label="Periodo precedente" data-testid="simstats-prev"><ChevronLeft className="w-4 h-4" /></Button>
    {input}
    <Button size="icon" variant="ghost" className="h-9 w-9" disabled={next > todayIso()} onClick={() => onChange(next)} aria-label="Periodo successivo" data-testid="simstats-next"><ChevronRight className="w-4 h-4" /></Button>
  </div>;
};

const dealersFor = (dealers, group) => (group === "direct" ? dealers.filter((d) => !d.master_id)
  : group.startsWith("master:") ? dealers.filter((d) => d.id === group.slice(7) || d.master_id === group.slice(7)) : group === "mine" ? [] : dealers);

export const SimStatsFilters = ({ f, set, options }) => {
  const dealers = dealersFor(options?.dealers || [], f.group);
  return <div className="space-y-3" data-testid="simstats-filters">
    <div className="flex flex-wrap items-center gap-2">
      <Segmented items={PERIODS} value={f.period} onChange={(period) => set({ period, gran: "" })} testid="simstats-period" />
      <AnchorInput period={f.period} value={f.date} onChange={(date) => set({ date })} />
      {["year", "12m", "all"].includes(f.period) && <Segmented items={[["", "Per mese"], ["day", "Per giorno"]]} value={f.gran} onChange={(gran) => set({ gran })} testid="simstats-gran" />}
    </div>
    <div className="flex flex-wrap items-center gap-2">
      <select value={f.operator} onChange={(e) => set({ operator: e.target.value })} className={sel} data-testid="simstats-operator"><option value="">Tutti gli operatori</option>{(options?.operators || []).map((o) => <option key={o.key} value={o.key}>{o.name}</option>)}</select>
      {options?.groups?.length > 0 && <select value={f.group} onChange={(e) => set({ group: e.target.value, dealer: "" })} className={sel} data-testid="simstats-group">{options.groups.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}</select>}
      {dealers.length > 0 && <select value={f.dealer} onChange={(e) => set({ dealer: e.target.value })} className={`${sel} sm:max-w-[16rem]`} data-testid="simstats-dealer"><option value="">Tutti i dealer</option>{dealers.map((d) => <option key={d.id} value={d.id}>{d.name}{d.is_master ? " (master)" : ""}</option>)}</select>}
      {(f.operator || f.group || f.dealer) && <Button size="sm" variant="ghost" onClick={() => set({ operator: "", group: "", dealer: "" })} data-testid="simstats-reset">Azzera filtri</Button>}
    </div>
  </div>;
};
